import FormField from "components/form/FormField";
import UserLabel from "components/form/UserLabel";
import { UserSelect } from "components/form/UserSelect";
import HelpTooltip from "components/general/HelpTooltip";
import { Box, FormattedCurrency, HStack, Input, InputField, Paragraph, Select, Text, Typography } from "components/ui";
import { Form, Formik } from "formik";
import { DollarSign } from "lucide-react-native";
import React, { useState } from "react";
import { TextInput } from "react-native";
import { Col, Container as FlexGridContainer, Row } from "react-native-flex-grid";
import { Group, useGetGlAccountQuery, usePutGroupByIdMutation, User } from "store/abcoApi";
import { useIsMobile } from "utils/hooks/useIsMobile";

interface GroupOptionsViewModeProps {
	group: Group;
	isEditMode: boolean;
}

const budgetOptions = [
	// { value: "per-month", label: "Per Month" },
	{ value: "month-by-month", label: "Month By Month" },
];

const currentYear = new Date().getFullYear();

const yearOptions = [
	{ value: currentYear - 1, label: `${currentYear - 1}` },
	{ value: currentYear, label: `${currentYear}` },
	{ value: currentYear + 1, label: `${currentYear + 1}` },
];

const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const GroupOptionsViewMode = ({ isEditMode, group }: GroupOptionsViewModeProps) => {
	const [putGroup, { data: putData, isLoading: putIsLoading, isError: putIsError, isSuccess: putIsSuccess }] = usePutGroupByIdMutation();
	const { data: glAccount, isLoading } = useGetGlAccountQuery({ pageSize: 9999 });
	const isMobile = useIsMobile();
	const [budget, setBudget] = useState<number | string>(group.budget || "");
	const [selectedBudgetType, setSelectedBudgetType] = useState(budgetOptions[0].value);
	const [selectedYear, setSelectedYear] = useState(currentYear);
	const [year, setYear] = useState({
		value: currentYear,
		label: `${currentYear}`,
	});

	function handleApproverChange(value: any) {
		const updatedGroup = {
			...group,
			approverCollection: value,
		};

		putGroup({ id: Number(group.id), group: updatedGroup });
	}

	function handleUpdateBudget(value: string | number) {
		if (value === "" || !isNaN(Number(value))) {
			setBudget(value);
			if (value !== "") {
				const newBudget = parseInt(value.toString(), 10);
				const updatedGroup = {
					...group,
					budget: newBudget,
				};
				putGroup({ id: Number(group.id), group: updatedGroup });
			}
		}
	}

	function handleYearChange(option: any) {
		setYear(option);
		setSelectedYear(option.value);
	}

	const allUsers = (group.userGroup ?? []).flatMap((userGroup) => userGroup.userCollection ?? []);

	if (isEditMode) {
		return (
			<>
				<Box position="relative" zIndex={99} gap={12} my={12}>
					<Col xs={isMobile ? "auto" : 4}>
						<Box flexDirection="row" alignItems="center" gap={8}>
							<Paragraph weight="bold">Approvers</Paragraph>
							<HelpTooltip tip="Only users selected in 'User Access and Control' will be displayed here" />
						</Box>
					</Col>
					<Col>
						<Box alignContent="center" flexDirection="row" alignItems="center" gap={8} flexWrap="wrap">
							<UserSelect
								isFluid
								value={group.approverCollection ?? []}
								options={allUsers}
								onSelect={(newValue) => {
									handleApproverChange(newValue ?? "");
								}}
							/>
						</Box>
					</Col>
				</Box>

				<Box my={12} ml={12}>
					<Row style={{ position: "relative", zIndex: 9 }}>
						<Box mb={48} gap={12} my={12} style={{ position: "relative", zIndex: 100 }}>
							<Col xs={isMobile ? "auto" : 4}>
								<Box flexDirection="row" alignItems="center" gap={8}>
									<Paragraph weight="bold">Budget</Paragraph>
									<HelpTooltip tip="Budget for this site" />
								</Box>
							</Col>
							<Col>
								<HStack gap={12}>
									<Box>
										<Select options={budgetOptions} onChange={(option) => setSelectedBudgetType(option.value)} value={budgetOptions.find((option) => option.value === selectedBudgetType)} isClearable={false} />
									</Box>
									<Box>
										<Select options={yearOptions} onChange={handleYearChange} value={yearOptions.find((option) => option.value === year.value)} isClearable={false} />
									</Box>

									{/* <Box alignContent="center" flexDirection="row" alignItems="center" gap={8} flexWrap="wrap">
									
										<Box position="relative">
											<Input sx={{ paddingBlock: 12, paddingHorizontal: 6 }} bg="$basicwhite" borderRadius={8} borderColor="$neutral300" variant="outline" size="md">
												<InputField onChangeText={handleUpdateBudget} onFocus={() => {}} onBlur={() => {}} value={budget.toString()} placeholder={"Type here..."} secureTextEntry={false} />
											</Input>
										</Box>
									</Box> */}
								</HStack>
							</Col>
						</Box>
					</Row>
					<Row style={{ position: "relative", zIndex: 8, marginBottom: 24 }}>
						<Col>
							<Typography weight={"bold"}>Month</Typography>
						</Col>
						{glAccount?.list?.map((glAccount) => {
							return (
								<Col key={glAccount.id}>
									<Typography weight={"bold"}>{glAccount.name}</Typography>
								</Col>
							);
						})}
					</Row>
					<Formik
						initialValues={group.monthlyBudgetCollection ?? {}}
						enableReinitialize={true}
						onSubmit={(values) => {
							var groupData = {
								...group,
								monthlyBudgetCollection: values,
							};
							putGroup({ id: Number(group.id), group: groupData });
						}}
					>
						{(formik) => {
							const { handleChange, handleBlur, values, submitForm } = formik;
							var yearKey = year.value ? year.value : year;
							return (
								<Form>
									<Box gap={12}>
										{Array.from({ length: 12 }, (_, i) => i + 1).map((month) => {
											const fieldName = `${yearKey}-${month}`;
											return (
												<Row key={fieldName}>
													<Col style={{ justifyContent: "center" }}>
														<Text>{monthNames[month - 1]}</Text>
													</Col>
													{glAccount?.list?.map((glAccount) => {
														var key = `${glAccount.id}-${fieldName}`;
														let monthValue = values[key] ?? "";

														return (
															<Col key={key}>
																<Box position="relative">
																	<Input sx={{ paddingBlock: 12, paddingHorizontal: 6 }} bg="$basicwhite" borderRadius={8} borderColor="$neutral300" variant="outline" size="md">
																		<DollarSign style={{ minWidth: 16, marginLeft: 4 }} size={16} color={"black"} />
																		<InputField
																			onBlur={(ev) => {
																				handleBlur(key)(ev);
																				submitForm(); // Auto-submits when input loses focus
																			}}
																			onChangeText={handleChange(key)}
																			value={monthValue.toString()}
																			placeholder={"Type here..."}
																		/>
																	</Input>
																</Box>
															</Col>
														);
													})}
												</Row>
											);
										})}
									</Box>
								</Form>
							);
						}}
					</Formik>
				</Box>
			</>
		);
	}

	return (
		<FlexGridContainer style={{ rowGap: 16 }} noPadding fluid>
			<Row style={{ flexDirection: isMobile ? "column" : "row" }} key={group.id}>
				<Col xs={isMobile ? "auto" : 4}>
					<Box flexDirection="row" alignItems="center" gap={8}>
						<Paragraph weight="bold">Approver</Paragraph>
						<HelpTooltip tip="User approving orders for this site" />
					</Box>
				</Col>
				<Col>
					<Box alignContent="center" flexDirection="row" alignItems="center" gap={8} flexWrap="wrap">
						{group.approverCollection && group.approverCollection.length > 0 ? (
							group.approverCollection.map((user: User, index) => {
								return <UserLabel key={index} firstname={user.firstName ?? ""} lastname={user.lastName ?? ""} />;
							})
						) : (
							<Box paddingHorizontal={12} paddingVertical={8}>
								<Text fontWeight={"$bold"} fontSize={12}>
									N/A
								</Text>
							</Box>
						)}
					</Box>
				</Col>
			</Row>
			<Row style={{ flexDirection: isMobile ? "column" : "row" }} key={group.id}>
				<Col xs={isMobile ? "auto" : 4}>
					<Box flexDirection="row" alignItems="center" gap={8}>
						<Paragraph weight="bold">Budget</Paragraph>
						<HelpTooltip tip="Budget for this site" />
					</Box>
				</Col>
				<Col>
					<Box alignContent="center" flexDirection="row" alignItems="center" gap={8} flexWrap="wrap">
						<Box paddingHorizontal={12} paddingVertical={8}>
							<Typography>
								<FormattedCurrency amount={group.budget} />{" "}
							</Typography>
						</Box>
					</Box>
				</Col>
			</Row>
		</FlexGridContainer>
	);
};

export default GroupOptionsViewMode;
